import React, { useLayoutEffect, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContactPageOutlinedIcon from '@mui/icons-material/ContactPageOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { help } from '../helpTexts';
import { launchColors, overlineSx, phasePalette } from '../launchTheme';
import InfoTip from '../ui/InfoTip';
import { CalendarWindow, YearbookProductMatch, YearbookRecipient, YearbookRow, YearbookWeekGroup } from '../types';
import { StatusPill } from '../ui/StatusPill';
import { athenaEan, athenaProductName } from './athenaName';
import ArticleNumberCell, { ArticlePatch } from './ArticleNumberCell';
import CategoryContactCell, { ContactCard, contactCards, countText, recipientsOf } from './CategoryContactCell';
import { CustomerContact } from './customerContacts';
import EditableCell from './EditableCell';
import { announcementHeading, COLUMN_LABELS, ISSUE_LABELS, revisionLabel, RowColumn, weekTitle } from './labels';
import SamplesCell, { ReminderPatch } from './SamplesCell';
import { SampleTotal } from './sampleTotals';

export type TextColumn = Exclude<RowColumn, 'articleNumber' | 'categoryContact'>;

const WIDTHS: Record<RowColumn, number> = {
  articleNumber: 150,
  articleName: 300,
  ean: 140,
  action: 150,
  info: 220,
  commentKam: 220,
  samples: 190,
  categoryContact: 230,
};

const CHECK_WIDTH = 44;
const DETAIL_WIDTH = 96;

interface Props {
  weeks: YearbookWeekGroup[];
  visible: RowColumn[];
  windows: CalendarWindow[];
  canEdit: boolean;
  canOpenProductInfo: boolean;
  canOpenAlcoholInfo?: boolean;
  products: Record<string, YearbookProductMatch>;
  selected: Set<string>;
  onSelect: (rowIds: string[], on: boolean) => void;
  onEditWeek: (week: YearbookWeekGroup) => void;
  onOpenRow: (row: YearbookRow) => void;
  onAddCategory: (week: YearbookWeekGroup) => void;
  onAddProduct: (week: YearbookWeekGroup, category: string) => void;
  onRenameCategory: (rows: YearbookRow[], name: string) => Promise<boolean>;
  onSaveText: (row: YearbookRow, column: TextColumn, value: string) => Promise<boolean>;
  onSaveReminder: (row: YearbookRow, patch: ReminderPatch) => Promise<boolean>;
  sampleTotals: Record<string, SampleTotal>;
  asOf: string;
  onSaveArticle: (row: YearbookRow, patch: ArticlePatch) => Promise<boolean>;
  onSaveRecipients: (row: YearbookRow, recipients: YearbookRecipient[]) => Promise<boolean>;
  onOpenCards: (cards: ContactCard[], index: number) => void;
  contacts: CustomerContact[];
  onDeleteRows: (rows: YearbookRow[]) => void;
  expanded: Set<string>;
  onToggleRecipients: (rowId: string) => void;
}

const CATEGORY_BG = '#f2efef';
const isProduct = (row: YearbookRow) => Boolean(row.articleName.trim() || row.articleNumber.trim());
const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

const headSx = {
  ...overlineSx,
  fontSize: 11,
  bgcolor: launchColors.section,
  borderBottom: `1px solid ${launchColors.divider}`,
  py: 1,
  whiteSpace: 'nowrap' as const,
};
const cellSx = { borderBottom: `1px solid ${launchColors.rowDivider}`, py: 0.75, px: 1.25, overflow: 'hidden' };

const SelectBox: React.FC<{ ids: string[]; selected: Set<string>; label: string; onSelect: Props['onSelect'] }> = ({ ids, selected, label, onSelect }) => {
  const count = ids.filter((id) => selected.has(id)).length;
  return (
    <Checkbox
      size="small"
      disabled={ids.length === 0}
      checked={ids.length > 0 && count === ids.length}
      indeterminate={count > 0 && count < ids.length}
      onChange={(event) => onSelect(ids, event.target.checked)}
      inputProps={{ 'aria-label': label }}
      sx={{ p: 0.5 }}
    />
  );
};

const WeekStatus: React.FC<{ window?: CalendarWindow }> = ({ window }) => {
  if (window?.yearbookChanged) return <StatusPill label="Ändrad efter publicering" fg={launchColors.warningText} bg={launchColors.warningBg} />;
  if (window) return <StatusPill label={`Publicerad version ${window.version}`} fg={launchColors.mossDark} bg={launchColors.mossLight} />;
  return <StatusPill label="Ej publicerad" fg={launchColors.textSecondary} bg={launchColors.card} border={`1px solid ${launchColors.divider}`} />;
};

type RowActions = Pick<Props, 'onSelect' | 'onOpenRow' | 'onSaveText' | 'onSaveReminder' | 'onSaveArticle' | 'onSaveRecipients' | 'onOpenCards' | 'onDeleteRows' | 'onToggleRecipients'>;

interface RowProps {
  row: YearbookRow;
  visible: RowColumn[];
  canEdit: boolean;
  canOpenProductInfo: boolean;
  canOpenAlcoholInfo: boolean;
  isSelected: boolean;
  expanded: boolean;
  match?: YearbookProductMatch;
  total?: SampleTotal;
  contacts: CustomerContact[];
  asOf: string;
  stripe: string;
  actions: RowActions;
}

const matchKey = (match?: YearbookProductMatch): string => {
  if (!match) return '';
  if (match.status === 'linked' || match.status === 'matched') return [match.status, match.assetId, match.title, match.hasThumbnail, match.ean ?? ''].join('|');
  if (match.status === 'catalog') return [match.status, match.productId, match.title, match.ean ?? '', match.source ?? ''].join('|');
  if (match.status === 'ambiguous') return `ambiguous|${match.options.map((option) => [option.assetId, option.title, option.articleNumber, option.hasThumbnail].join(':')).join(',')}`;
  return 'none';
};

const sameRow = (prev: RowProps, next: RowProps): boolean => prev.row.id === next.row.id
  && prev.row.version === next.row.version
  && prev.isSelected === next.isSelected
  && prev.expanded === next.expanded
  && prev.canEdit === next.canEdit
  && prev.canOpenProductInfo === next.canOpenProductInfo
  && prev.canOpenAlcoholInfo === next.canOpenAlcoholInfo
  && prev.visible === next.visible
  && prev.contacts === next.contacts
  && prev.asOf === next.asOf
  && prev.stripe === next.stripe
  && prev.actions === next.actions
  && matchKey(prev.match) === matchKey(next.match)
  && JSON.stringify(prev.total ?? null) === JSON.stringify(next.total ?? null);

const RecipientRows: React.FC<{ row: YearbookRow; visible: RowColumn[]; canEdit: boolean; contacts: CustomerContact[]; stripe: string; onOpenCards: Props['onOpenCards'] }> = ({
  row, visible, canEdit, contacts, stripe, onOpenCards,
}) => {
  const recipients = recipientsOf(row);
  const cards = contactCards(row, contacts);
  const product = row.articleName || row.articleNumber || 'produkten';
  return (
    <>
      {recipients.map((recipient, index) => {
        const contact = contacts.find((item) => item.id === recipient.contactId);
        const cardIndex = cards.findIndex((card) => card.contact.id === recipient.contactId);
        return (
          <TableRow key={recipient.contactId} aria-label={`Varuprov ${index + 1} av ${recipients.length} för ${product}`} sx={{ bgcolor: launchColors.page }}>
            {canEdit && <TableCell sx={{ ...cellSx, borderLeft: stripe }} />}
            {visible.map((column) => (
              <TableCell key={column} sx={{ ...cellSx, py: 0.5 }}>
                {column === 'categoryContact' ? (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, pl: 1.5 }}>
                    <SubdirectoryArrowRightIcon sx={{ fontSize: 16, color: launchColors.textMuted }} />
                    <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: contact ? launchColors.navy : launchColors.textSecondary }}>
                      {contact?.name || 'Saknas'}
                    </Typography>
                    {!visible.includes('samples') && (
                      <Typography noWrap sx={{ flexShrink: 0, fontSize: 13, fontWeight: 700, color: launchColors.navy, fontVariantNumeric: 'tabular-nums' }}>{countText(recipient.count)}</Typography>
                    )}
                    {contact && (
                      <Tooltip title="Visa kontaktuppgifter">
                        <IconButton size="small" aria-label={`Kontaktuppgifter för ${contact.name}`} onClick={() => onOpenCards(cards, Math.max(cardIndex, 0))} sx={{ color: launchColors.navy }}>
                          <ContactPageOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                ) : column === 'samples' ? (
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: launchColors.navy, fontVariantNumeric: 'tabular-nums' }}>{countText(recipient.count)}</Typography>
                ) : column === 'articleName' ? (
                  <Typography sx={{ ...overlineSx, fontSize: 11 }}>{`Mottagare ${index + 1} av ${recipients.length}`}</Typography>
                ) : null}
              </TableCell>
            ))}
            <TableCell sx={cellSx} />
          </TableRow>
        );
      })}
    </>
  );
};

const YearbookTableRow = React.memo<RowProps>(({ row, visible, canEdit, canOpenProductInfo, canOpenAlcoholInfo, isSelected, expanded, match, total, contacts, asOf, stripe, actions }) => {
  const productName = athenaProductName(match);
  const ean = athenaEan(match);
  const recipientCount = recipientsOf(row).length;
  return (
    <>
    <TableRow
      hover
      selected={isSelected}
      sx={{
        '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'rgba(23,44,99,0.06)' },
        boxShadow: row.issues.length ? `inset 3px 0 0 ${launchColors.warningMarker}` : undefined,
      }}
    >
      {canEdit && (
        <TableCell sx={{ ...cellSx, px: 1.25, borderLeft: stripe }}>
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={(event) => actions.onSelect([row.id], event.target.checked)}
            inputProps={{ 'aria-label': `Markera ${row.articleName || row.articleNumber || 'rad'}` }}
            sx={{ p: 0.5 }}
          />
        </TableCell>
      )}
      {visible.map((column) => (
        <TableCell key={column} sx={cellSx}>
          {column === 'articleNumber' ? (
            <ArticleNumberCell row={row} match={match} editable={canEdit} canOpenProductInfo={canOpenProductInfo} canOpenAlcoholInfo={canOpenAlcoholInfo} onSave={(patch) => actions.onSaveArticle(row, patch)} />
          ) : column === 'categoryContact' ? (
            <CategoryContactCell
              row={row}
              contacts={contacts}
              canEdit={canEdit}
              expanded={expanded}
              onToggle={() => actions.onToggleRecipients(row.id)}
              onSave={(recipients) => actions.onSaveRecipients(row, recipients)}
              onOpen={actions.onOpenCards}
            />
          ) : column === 'samples' ? (
            <SamplesCell
              row={row}
              total={total}
              editable={canEdit}
              countLocked={recipientCount > 0}
              asOf={asOf}
              onSave={(value) => actions.onSaveText(row, 'samples', value)}
              onReminder={(patch) => actions.onSaveReminder(row, patch)}
            />
          ) : column === 'ean' ? (
            <EditableCell
              value={ean || row.ean || ''}
              label={COLUMN_LABELS.ean}
              editable={canEdit && !ean}
              maxLength={20}
              onSave={(value) => actions.onSaveText(row, 'ean', value)}
            />
          ) : (
            <EditableCell
              value={column === 'articleName' ? (productName || row.articleName) : row[column]}
              label={COLUMN_LABELS[column]}
              editable={canEdit && !(column === 'articleName' && productName)}
              maxLength={500}
              strong={column === 'articleName'}
              onSave={(value) => actions.onSaveText(row, column, value)}
            />
          )}
        </TableCell>
      ))}
      <TableCell sx={{ ...cellSx, px: 0.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="flex-end">
          {row.issues.length > 0 && (
            <Tooltip title={row.issues.map((issue) => ISSUE_LABELS[issue] || issue).join(' · ')}>
              <WarningAmberIcon aria-label="Behöver ses över" sx={{ fontSize: 18, color: launchColors.warningMarker }} />
            </Tooltip>
          )}
          {canEdit && isSelected && (
            <IconButton size="small" aria-label={`Radera ${row.articleName || row.articleNumber || 'rad'}`} onClick={() => actions.onDeleteRows([row])}>
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <IconButton size="small" aria-label="Öppna rad" onClick={() => actions.onOpenRow(row)}>
            <OpenInFullIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
    {expanded && recipientCount > 1 && (
      <RecipientRows row={row} visible={visible} canEdit={canEdit} contacts={contacts} stripe={stripe} onOpenCards={actions.onOpenCards} />
    )}
    </>
  );
}, sameRow);
YearbookTableRow.displayName = 'YearbookTableRow';

const YearbookTable: React.FC<Props> = ({
  weeks, visible, windows, canEdit, canOpenProductInfo, canOpenAlcoholInfo = false, products, selected, onSelect, onEditWeek, onOpenRow, onAddCategory, onAddProduct, onRenameCategory, onSaveText, onSaveReminder, sampleTotals, asOf, onSaveArticle, onSaveRecipients, onOpenCards, contacts, onDeleteRows, expanded, onToggleRecipients,
}) => {
  const latest = useRef<RowActions>({ onSelect, onOpenRow, onSaveText, onSaveReminder, onSaveArticle, onSaveRecipients, onOpenCards, onDeleteRows, onToggleRecipients });
  useLayoutEffect(() => {
    latest.current = { onSelect, onOpenRow, onSaveText, onSaveReminder, onSaveArticle, onSaveRecipients, onOpenCards, onDeleteRows, onToggleRecipients };
  });
  const actions = useMemo<RowActions>(() => ({
    onSelect: (ids, on) => latest.current.onSelect(ids, on),
    onOpenRow: (row) => latest.current.onOpenRow(row),
    onSaveText: (row, column, value) => latest.current.onSaveText(row, column, value),
    onSaveReminder: (row, patch) => latest.current.onSaveReminder(row, patch),
    onSaveArticle: (row, patch) => latest.current.onSaveArticle(row, patch),
    onSaveRecipients: (row, recipients) => latest.current.onSaveRecipients(row, recipients),
    onOpenCards: (cards, index) => latest.current.onOpenCards(cards, index),
    onDeleteRows: (rows) => latest.current.onDeleteRows(rows),
    onToggleRecipients: (rowId) => latest.current.onToggleRecipients(rowId),
  }), []);
  const span = visible.length + 1 + (canEdit ? 1 : 0);
  const allIds = weeks.flatMap((week) => week.categories.flatMap((category) => category.rows.map((row) => row.id)));
  const minWidth = visible.reduce((sum, column) => sum + WIDTHS[column], DETAIL_WIDTH + (canEdit ? CHECK_WIDTH : 0));
  return (
    <TableContainer sx={{ maxHeight: 'calc(100vh - 240px)', border: `1px solid ${launchColors.divider}`, borderRadius: '12px', bgcolor: launchColors.card }}>
      <Table stickyHeader size="small" aria-label="Årskalender" sx={{ tableLayout: 'fixed', minWidth }}>
        <colgroup>
          {canEdit && <col style={{ width: CHECK_WIDTH }} />}
          {visible.map((column) => <col key={column} style={{ width: WIDTHS[column] }} />)}
          <col style={{ width: DETAIL_WIDTH }} />
        </colgroup>
        <TableHead>
          <TableRow>
            {canEdit && (
              <TableCell sx={{ ...headSx, px: 1.25, py: 0.5 }}>
                <SelectBox ids={allIds} selected={selected} label="Markera alla rader" onSelect={onSelect} />
              </TableCell>
            )}
            {visible.map((column) => (
              <TableCell key={column} sx={headSx}>
                {column === 'samples' ? (
                  <Stack direction="row" alignItems="center" spacing={0.25}>
                    <span>{COLUMN_LABELS[column]}</span>
                    <InfoTip text={help.yearbookSamples} />
                  </Stack>
                ) : COLUMN_LABELS[column]}
              </TableCell>
            ))}
            <TableCell sx={headSx}><Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Detaljer</Box></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {weeks.map((week, index) => {
            const window = windows.find((item) => item.familyId === week.windowFamilyId);
            const weekIds = week.categories.flatMap((category) => category.rows.map((row) => row.id));
            const color = phasePalette[index % phasePalette.length];
            const previous = weeks[index - 1];
            const sameAnnouncement = Boolean(week.announcementDate && previous?.announcementDate === week.announcementDate);
            const revision = revisionLabel(week);
            const contentSpan = canEdit ? span - 1 : span;
            const stripe = `4px solid ${color}`;
            return (
              <React.Fragment key={week.weekKey}>
                {index > 0 && (
                  <TableRow>
                    <TableCell colSpan={span} sx={{ height: 16, p: 0, border: 0, bgcolor: launchColors.page }} />
                  </TableRow>
                )}
                <TableRow>
                  {canEdit && (
                    <TableCell sx={{ ...cellSx, px: 1.25, py: 1.75, bgcolor: launchColors.softNavy, borderLeft: stripe, borderBottom: `1px solid ${launchColors.divider}` }}>
                      <SelectBox ids={weekIds} selected={selected} label={`Markera ${weekTitle(week)}`} onSelect={onSelect} />
                    </TableCell>
                  )}
                  <TableCell colSpan={contentSpan} sx={{ bgcolor: launchColors.softNavy, borderBottom: `1px solid ${launchColors.divider}`, borderLeft: canEdit ? undefined : stripe, py: 1.75, px: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} useFlexGap flexWrap="wrap">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography component="h3" sx={{ fontSize: 19, fontWeight: 700, lineHeight: 1.25, color: launchColors.navy }}>{announcementHeading(week)}</Typography>
                        {sameAnnouncement && <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>Samma datum, annan revideringsvecka</Typography>}
                      </Box>
                      {revision && (
                        <Box sx={{ px: 1.25, py: 0.5, borderRadius: '8px', border: `2px solid ${color}`, bgcolor: launchColors.card, fontSize: 14, fontWeight: 700, color: launchColors.navy, whiteSpace: 'nowrap' }}>
                          {revision}
                        </Box>
                      )}
                      <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>
                        {`${plural(week.categories.length, 'kategori', 'kategorier')} · ${plural(week.productCount, 'produkt', 'produkter')}`}
                      </Typography>
                      <WeekStatus window={window} />
                      <Box sx={{ flex: 1 }} />
                      {canEdit && <Button size="small" startIcon={<AddIcon />} onClick={() => onAddCategory(week)}>Lägg till kategori</Button>}
                      {canEdit && <Button size="small" onClick={() => onEditWeek(week)}>Redigera vecka</Button>}
                    </Stack>
                  </TableCell>
                </TableRow>
                {week.categories.map((category) => {
                  const rows = category.rows;
                  const ids = rows.map((row) => row.id);
                  const productCount = rows.filter(isProduct).length;
                  const countLabel = productCount === 1 ? '1 produkt' : `${productCount} produkter`;
                  const categoryName = category.category === 'Utan kategori' ? '' : category.category;
                  return (
                    <React.Fragment key={`${week.weekKey}:${category.category}`}>
                      <TableRow>
                        {canEdit && (
                          <TableCell sx={{ ...cellSx, px: 1.25, bgcolor: CATEGORY_BG, borderLeft: stripe }}>
                            <SelectBox ids={ids} selected={selected} label={`Markera ${category.category}`} onSelect={onSelect} />
                          </TableCell>
                        )}
                        <TableCell colSpan={contentSpan} sx={{ ...cellSx, bgcolor: CATEGORY_BG, py: 0.75, px: 1.5, borderLeft: canEdit ? undefined : stripe }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={{ minWidth: 140, maxWidth: 360 }}>
                              <EditableCell
                                value={categoryName}
                                label="Kategori"
                                editable={canEdit}
                                maxLength={80}
                                strong
                                onSave={(value) => onRenameCategory(rows, value)}
                              />
                            </Box>
                            <Typography sx={{ ...overlineSx, color: launchColors.navy, whiteSpace: 'nowrap' }}>{countLabel}</Typography>
                            <Box sx={{ flex: 1 }} />
                            {canEdit && (
                              <Button size="small" startIcon={<AddIcon />} onClick={() => onAddProduct(week, category.category === 'Utan kategori' ? '' : category.category)}>
                                Lägg till produkt
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {rows.map((row) => (
                        <YearbookTableRow
                          key={row.id}
                          row={row}
                          visible={visible}
                          canEdit={canEdit}
                          canOpenProductInfo={canOpenProductInfo}
                          canOpenAlcoholInfo={canOpenAlcoholInfo}
                          isSelected={selected.has(row.id)}
                          expanded={expanded.has(row.id)}
                          match={products[row.id]}
                          total={sampleTotals[row.id]}
                          contacts={contacts}
                          asOf={asOf}
                          stripe={stripe}
                          actions={actions}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default YearbookTable;
